import {
  extractPreviewNationalPhone,
  normalizePreviewCountrySelection
} from "./previewCountryPhone";

export const MAX_PHOTO_SIZE = 2 * 1024 * 1024;
export const OWNED_CAREGIVER_STORAGE_KEY = "previewMapOwnedCaregivers";
export const PREVIEW_OWNER_KEY_STORAGE_KEY = "previewMapOwnerKey";
export const PREVIEW_MAP_DRAFT_STORAGE_KEY = "previewMapDraft";
export const PREVIEW_EDIT_SESSION_STORAGE_KEY = "previewMapEditSession";
export const COUNTRY_CONFIRMATION_LABEL = "Country to confirm";

export const normalizePreviewEmail = (value) =>
  String(value || "").trim().toLowerCase();

export const isValidPreviewEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizePreviewEmail(value));

export const isValidPreviewYouTubeUrl = (value) => {
  try {
    const url = new URL(String(value || "").trim());
    const hostname = url.hostname.replace(/^www\./, "").toLowerCase();
    if (hostname === "youtu.be") return Boolean(url.pathname.split("/").filter(Boolean)[0]);
    if (!["youtube.com", "m.youtube.com"].includes(hostname)) return false;
    if (url.pathname === "/watch") return Boolean(url.searchParams.get("v"));
    const [, type, id] = url.pathname.split("/");
    return Boolean(["shorts", "embed"].includes(type) && id);
  } catch {
    return false;
  }
};

export const getPreviewYouTubeEmbedUrl = (value) => {
  if (!isValidPreviewYouTubeUrl(value)) return "";

  try {
    const url = new URL(String(value || "").trim());
    const hostname = url.hostname.replace(/^www\./, "").toLowerCase();
    let videoId = "";

    if (hostname === "youtu.be") {
      [videoId] = url.pathname.split("/").filter(Boolean);
    } else if (url.pathname === "/watch") {
      videoId = url.searchParams.get("v") || "";
    } else {
      const [, type, id] = url.pathname.split("/");
      if (["shorts", "embed"].includes(type)) videoId = id || "";
    }

    return videoId
      ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0`
      : "";
  } catch {
    return "";
  }
};

const hasValue = (value) => String(value ?? "").trim().length > 0;

export const getPreviewProfileCompletion = (profile = {}) => {
  const requirements = [
    ["role", "Caregiver role", String(profile.role || "").toLowerCase() === "caregiver"],
    ["firstName", "First name", hasValue(profile.firstName)],
    ["lastName", "Last name", hasValue(profile.lastName)],
    ["country", "Confirmed country", hasValue(profile.country) && profile.country !== COUNTRY_CONFIRMATION_LABEL],
    ["city", "City or town", hasValue(profile.city)],
    ["phone", "Phone / WhatsApp", hasValue(profile.phone)],
    ["email", "Email address", isValidPreviewEmail(profile.email)],
    ["profilePhoto", "Profile photo", hasValue(profile.profilePhoto)],
    ["spokenLanguage", "Spoken language", hasValue(profile.spokenLanguage)],
    ["gender", "Gender", ["M", "F"].includes(profile.gender)],
    ["age", "Age", Number(profile.age) >= 18 && Number(profile.age) <= 120],
    ["paymentMode", "Payment mode", hasValue(profile.paymentMode)],
    ["rate", "Hourly rate", ["1", "2", "3", "4", "5", "6"].includes(String(profile.rate))],
    ["status", "Availability", ["available", "unavailable"].includes(profile.status)],
    ["introVideoUrl", "YouTube introduction video", isValidPreviewYouTubeUrl(profile.introVideoUrl)]
  ];
  const missingFields = requirements
    .filter(([, , complete]) => !complete)
    .map(([key, label]) => ({ key, label }));

  return {
    profileComplete: missingFields.length === 0,
    profileCompletionPercent: Math.round(
      ((requirements.length - missingFields.length) / requirements.length) * 100
    ),
    missingProfileFields: missingFields
  };
};

export const createEmptyPreviewMapForm = () => ({
  firstName: "",
  lastName: "",
  country: "",
  role: "caregiver",
  phone: "",
  email: "",
  profilePhoto: "",
  spokenLanguage: "",
  gender: "",
  age: "",
  city: "",
  paymentMode: "",
  rate: "",
  status: "available",
  professionalTitle: "",
  bio: "",
  introVideoUrl: "",
  yearsExperience: "",
  specialties: "",
  certifications: "",
  availabilityNotes: "",
  experienceEntries: [],
  latitude: "",
  longitude: ""
});

export const normalizePreviewMapFormFields = (form = {}) => {
  const country =
    String(form.country || "").trim() === COUNTRY_CONFIRMATION_LABEL
      ? ""
      : normalizePreviewCountrySelection(form.country);

  return {
    ...createEmptyPreviewMapForm(),
    ...form,
    country,
    phone: extractPreviewNationalPhone(form.phone, country)
  };
};

export const createPreviewMapFormFromCaregiver = (caregiver = {}) =>
  normalizePreviewMapFormFields({
    firstName: caregiver.firstName || "",
    lastName: caregiver.lastName || "",
    country: caregiver.country || "",
    role: caregiver.role || "",
    phone: caregiver.phone || "",
    email: caregiver.email || "",
    profilePhoto: caregiver.profilePhoto || "",
    spokenLanguage: caregiver.spokenLanguage || caregiver.language || "",
    gender: caregiver.gender || "",
    age: caregiver.age ?? "",
    city: caregiver.city || "",
    paymentMode: caregiver.paymentMode || caregiver.paymentMethod || "",
    rate: caregiver.rate ?? "",
    status: caregiver.status || "available",
    professionalTitle: caregiver.professionalTitle || "",
    bio: caregiver.bio || "",
    introVideoUrl: caregiver.introVideoUrl || "",
    yearsExperience: caregiver.yearsExperience ?? "",
    specialties: caregiver.specialties || "",
    certifications: caregiver.certifications || "",
    availabilityNotes: caregiver.availabilityNotes || "",
    experienceEntries: Array.isArray(caregiver.experienceEntries)
      ? caregiver.experienceEntries
      : [],
    latitude:
      caregiver.latitude === null || caregiver.latitude === undefined
        ? ""
        : String(caregiver.latitude),
    longitude:
      caregiver.longitude === null || caregiver.longitude === undefined
        ? ""
        : String(caregiver.longitude)
  });

export const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image."));
    reader.readAsDataURL(file);
  });

export const compressImage = (dataUrl, maxWidth = 520, quality = 0.8) =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const scale = Math.min(1, maxWidth / image.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Image processing is unavailable."));
        return;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };

    image.onerror = () => reject(new Error("Failed to process image."));
    image.src = dataUrl;
  });

export const readOwnedCaregiverTokens = () => {
  if (typeof window === "undefined") return {};

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(OWNED_CAREGIVER_STORAGE_KEY) || "{}"
    );

    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

export const writeOwnedCaregiverTokens = (tokens) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    OWNED_CAREGIVER_STORAGE_KEY,
    JSON.stringify(tokens)
  );
};

export const getPreviewOwnerKey = () => {
  if (typeof window === "undefined") return "";

  const existing = window.localStorage.getItem(PREVIEW_OWNER_KEY_STORAGE_KEY);
  if (existing) return existing;

  const nextValue =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `preview-owner-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  window.localStorage.setItem(PREVIEW_OWNER_KEY_STORAGE_KEY, nextValue);
  return nextValue;
};

export const readPreviewMapDraft = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(PREVIEW_MAP_DRAFT_STORAGE_KEY) || "null"
    );

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      draftKey: parsed.draftKey || "",
      form: normalizePreviewMapFormFields(parsed.form)
    };
  } catch {
    return null;
  }
};

export const writePreviewMapDraft = (draftKey, form) => {
  if (typeof window === "undefined") return;

  window.sessionStorage.setItem(
    PREVIEW_MAP_DRAFT_STORAGE_KEY,
    JSON.stringify({
      draftKey,
      form: normalizePreviewMapFormFields(form)
    })
  );
};

export const clearPreviewMapDraft = (draftKey = "") => {
  if (typeof window === "undefined") return;

  const existingDraft = readPreviewMapDraft();
  if (draftKey && existingDraft?.draftKey && existingDraft.draftKey !== draftKey) {
    return;
  }

  window.sessionStorage.removeItem(PREVIEW_MAP_DRAFT_STORAGE_KEY);
};

export const readPreviewEditSession = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const savedSession =
      window.localStorage.getItem(PREVIEW_EDIT_SESSION_STORAGE_KEY) ||
      window.sessionStorage.getItem(PREVIEW_EDIT_SESSION_STORAGE_KEY);
    const parsed = JSON.parse(savedSession || "null");

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    const normalizedSession = {
      caregiverID: String(parsed.caregiverID || ""),
      token: String(parsed.token || "")
    };

    if (!window.localStorage.getItem(PREVIEW_EDIT_SESSION_STORAGE_KEY)) {
      window.localStorage.setItem(
        PREVIEW_EDIT_SESSION_STORAGE_KEY,
        JSON.stringify(normalizedSession)
      );
      window.sessionStorage.removeItem(PREVIEW_EDIT_SESSION_STORAGE_KEY);
    }

    return normalizedSession;
  } catch {
    return null;
  }
};

export const writePreviewEditSession = (session) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    PREVIEW_EDIT_SESSION_STORAGE_KEY,
    JSON.stringify({
      caregiverID: String(session?.caregiverID || ""),
      token: String(session?.token || "")
    })
  );
  window.sessionStorage.removeItem(PREVIEW_EDIT_SESSION_STORAGE_KEY);
};

export const clearPreviewEditSession = (caregiverID = "") => {
  if (typeof window === "undefined") return;

  const existingSession = readPreviewEditSession();
  if (
    caregiverID &&
    existingSession?.caregiverID &&
    existingSession.caregiverID !== caregiverID
  ) {
    return;
  }

  window.localStorage.removeItem(PREVIEW_EDIT_SESSION_STORAGE_KEY);
  window.sessionStorage.removeItem(PREVIEW_EDIT_SESSION_STORAGE_KEY);
};

export const formatPreviewCaregiverLocation = (caregiver) =>
  [caregiver?.city, caregiver?.country].filter(Boolean).join(", ") ||
  caregiver?.country ||
  "";

export const formatPreviewLocationPrecision = (precision) => {
  if (!precision) return "Approximate map area";

  return {
    "exact pin": "Exact map pin",
    district: "District level",
    "city/district": "City or district level",
    "state/province": "State or province level",
    country: "Country level",
    "needs-confirmation": "Needs confirmation",
    approximate: "Approximate map area",
    saved: "Saved location"
  }[precision] || "Approximate map area";
};

export const formatPreviewRole = (role) => {
  const normalized = String(role || "").trim().toLowerCase();

  return {
    caregiver: "Caregiver",
    elder: "Elder",
    "kin-keeper": "Kin-keeper"
  }[normalized] || (role ? String(role).trim() : "");
};

export const isConfirmedPreviewCountry = (country) =>
  Boolean(
    String(country || "").trim() &&
      String(country || "").trim() !== COUNTRY_CONFIRMATION_LABEL
  );

export const parseCoordinateInput = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const hasExactPreviewCoordinates = (form) =>
  parseCoordinateInput(form?.latitude) !== null &&
  parseCoordinateInput(form?.longitude) !== null;
